<template>
  <div class="crm-board flex flex-col h-full">
    <!-- Board Toolbar -->
    <div class="flex items-center gap-3 px-4 py-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
      <!-- Pipeline selector -->
      <div class="flex items-center gap-2">
        <span class="i-lucide-kanban size-4 text-woot-500" />
        <select
          v-model="selectedPipelineId"
          class="text-sm font-semibold text-slate-700 dark:text-slate-200 bg-transparent border-none outline-none cursor-pointer"
          @change="onPipelineChange"
        >
          <option v-for="p in pipelines" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
      </div>

      <div class="h-5 w-px bg-slate-200 dark:bg-slate-700" />

      <!-- Filters -->
      <div class="flex items-center gap-2">
        <button
          class="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
          :class="{ 'bg-woot-50 border-woot-300 text-woot-600': filters.assigneeId }"
          @click="showFilterPanel = !showFilterPanel"
        >
          <span class="i-lucide-filter size-3.5" />
          Filtros
          <span v-if="activeFilterCount > 0" class="bg-woot-500 text-white rounded-full px-1.5 text-xs">{{ activeFilterCount }}</span>
        </button>

        <button
          v-if="activeFilterCount > 0"
          class="text-xs text-slate-500 hover:text-red-500 transition-colors"
          @click="clearFilters"
        >
          Limpar
        </button>
      </div>

      <div class="ml-auto flex items-center gap-2">
        <!-- Add stage button -->
        <button
          class="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
          @click="$emit('add-stage', currentPipeline)"
        >
          <span class="i-lucide-columns size-3.5" />
          Nova coluna
        </button>

        <!-- Add card button -->
        <button
          class="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-woot-500 hover:bg-woot-600 text-white transition-colors"
          @click="$emit('add-card', null)"
        >
          <span class="i-lucide-plus size-3.5" />
          Novo card
        </button>
      </div>
    </div>

    <!-- Filter panel -->
    <div
      v-if="showFilterPanel"
      class="px-4 py-3 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 flex items-center gap-4 flex-shrink-0"
    >
      <div class="flex items-center gap-2">
        <label class="text-xs text-slate-500 font-medium">Agente:</label>
        <select
          v-model="filters.assigneeId"
          class="text-xs px-2 py-1 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300"
        >
          <option :value="null">Todos</option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
        </select>
      </div>

      <div class="flex items-center gap-2">
        <label class="text-xs text-slate-500 font-medium">Prioridade:</label>
        <select
          v-model="filters.priority"
          class="text-xs px-2 py-1 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300"
        >
          <option value="">Todas</option>
          <option value="urgent">Urgente</option>
          <option value="high">Alta</option>
          <option value="medium">Média</option>
          <option value="low">Baixa</option>
        </select>
      </div>

      <div class="flex items-center gap-2">
        <label class="text-xs text-slate-500 font-medium">Buscar:</label>
        <input
          v-model="filters.search"
          type="text"
          placeholder="Nome do card ou contato..."
          class="text-xs px-2 py-1 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 w-48"
        />
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="isLoading" class="flex-1 flex items-center justify-center">
      <div class="flex flex-col items-center gap-3 text-slate-400">
        <span class="i-lucide-loader-2 size-8 animate-spin" />
        <span class="text-sm">Carregando board...</span>
      </div>
    </div>

    <!-- Empty pipeline state -->
    <div v-else-if="!currentPipeline" class="flex-1 flex items-center justify-center">
      <div class="flex flex-col items-center gap-4 text-slate-400 max-w-sm text-center">
        <span class="i-lucide-kanban size-16 opacity-30" />
        <div>
          <h3 class="text-base font-semibold text-slate-600 dark:text-slate-300 mb-1">Nenhum pipeline</h3>
          <p class="text-sm">Crie um pipeline para começar a organizar seus negócios.</p>
        </div>
        <button
          class="flex items-center gap-2 px-4 py-2 bg-woot-500 hover:bg-woot-600 text-white rounded-lg text-sm transition-colors"
          @click="$emit('create-pipeline')"
        >
          <span class="i-lucide-plus size-4" />
          Criar pipeline
        </button>
      </div>
    </div>

    <!-- Kanban columns -->
    <div
      v-else
      class="flex-1 overflow-x-auto"
    >
      <div class="flex gap-3 p-4 h-full" style="min-width: max-content;">
        <KanbanColumn
          v-for="stage in currentStages"
          :key="stage.id"
          :stage="stage"
          :cards="getFilteredCards(stage.id)"
          @add-card="$emit('add-card', $event)"
          @edit-stage="$emit('edit-stage', $event)"
          @card-click="$emit('card-click', $event)"
          @edit-card="$emit('edit-card', $event)"
          @delete-card="$emit('delete-card', $event)"
          @card-dropped="onCardDropped"
        />

        <!-- Add column placeholder -->
        <div
          class="flex-shrink-0 w-72 flex items-center justify-center rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 cursor-pointer hover:border-woot-400 hover:bg-woot-50 dark:hover:bg-woot-900/10 transition-colors group"
          @click="$emit('add-stage', currentPipeline)"
        >
          <div class="flex flex-col items-center gap-2 text-slate-400 group-hover:text-woot-500 transition-colors">
            <span class="i-lucide-plus size-6" />
            <span class="text-sm font-medium">Nova coluna</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import KanbanColumn from './KanbanColumn.vue';

const props = defineProps({
  agents: { type: Array, default: () => [] },
});

const emit = defineEmits(['add-card', 'add-stage', 'edit-stage', 'card-click', 'edit-card', 'delete-card', 'create-pipeline']);

const store = useStore();
const showFilterPanel = ref(false);
const filters = ref({ assigneeId: null, priority: '', search: '' });

const pipelines = computed(() => store.getters['crm/getPipelines']);
const currentPipeline = computed(() => store.getters['crm/getCurrentPipeline']);
const isLoading = computed(() => store.getters['crm/getUIFlags'].isFetchingPipelines || store.getters['crm/getUIFlags'].isFetchingCards);

const selectedPipelineId = ref(null);

const currentStages = computed(() => {
  if (!currentPipeline.value) return [];
  return (currentPipeline.value.stages || currentPipeline.value.crm_stages || []).sort((a, b) => a.position - b.position);
});

const activeFilterCount = computed(() => {
  let count = 0;
  if (filters.value.assigneeId) count++;
  if (filters.value.priority) count++;
  if (filters.value.search) count++;
  return count;
});

function getFilteredCards(stageId) {
  let cards = store.getters['crm/getCardsByStage'](stageId);

  if (filters.value.assigneeId) {
    cards = cards.filter(c => c.assignee_id === filters.value.assigneeId);
  }
  if (filters.value.priority) {
    cards = cards.filter(c => c.priority === filters.value.priority);
  }
  if (filters.value.search) {
    const q = filters.value.search.toLowerCase();
    cards = cards.filter(c =>
      c.title?.toLowerCase().includes(q) ||
      c.contact?.name?.toLowerCase().includes(q)
    );
  }

  return cards;
}

function clearFilters() {
  filters.value = { assigneeId: null, priority: '', search: '' };
}

function onPipelineChange() {
  const pipeline = pipelines.value.find(p => p.id === selectedPipelineId.value);
  if (pipeline) {
    store.dispatch('crm/setCurrentPipeline', pipeline);
    loadCards();
  }
}

async function loadCards() {
  if (!currentPipeline.value) return;
  await store.dispatch('crm/fetchCards', { pipelineId: currentPipeline.value.id });
}

async function onCardDropped({ cardId, fromStageId, toStageId, position }) {
  if (!currentPipeline.value) return;
  const allCards = store.getters['crm/getAllCards'];
  const card = allCards.find(c => c.id === cardId);
  if (!card) return;

  await store.dispatch('crm/moveCard', {
    pipelineId: currentPipeline.value.id,
    cardId,
    fromStageId,
    toStageId,
    position,
    card,
  });
}

watch(currentPipeline, (pipeline) => {
  if (pipeline) {
    selectedPipelineId.value = pipeline.id;
    loadCards();
  }
});

onMounted(async () => {
  await store.dispatch('crm/fetchPipelines');
  if (currentPipeline.value) {
    selectedPipelineId.value = currentPipeline.value.id;
    await loadCards();
  }
});
</script>
